import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoginResponse } from '@api/auth';
import { LOCAL_STORAGE_USER_SLICE_KEY } from '@shared/constants';
import {
  getLocalStorageAuthState,
  setLastActiveUser,
  setLoaclStorageAuthState,
} from '@utils/auth';
import {
  loginUserAction,
  refreshAccessTokenAction,
  registerUserAction,
} from '@data/actions/authActions';

export interface AuthInitialState {
  isLoading: boolean;
  errors: Record<string, string[]>;
  user: LoginResponse['user'] | null;
  accessToken: LoginResponse['accessToken'] | null;
  refreshToken: LoginResponse['refreshToken'] | null;
}

const handleLogin = (
  state: AuthInitialState,
  action: PayloadAction<LoginResponse | undefined>
) => {
  state.isLoading = false;
  if (!action.payload) {
    return;
  }

  state.user = action.payload.user;
  state.accessToken = action.payload.accessToken;
  state.refreshToken = action.payload.refreshToken;

  setLastActiveUser(action.payload.user.id);
  setLoaclStorageAuthState(action.payload);
};
const initialState: AuthInitialState = {
  errors: {},
  isLoading: false,
  ...getLocalStorageAuthState(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: state => {
      localStorage.removeItem(LOCAL_STORAGE_USER_SLICE_KEY);
      return {
        ...state,
        accessToken: null,
        refreshToken: null,
        user: null,
      };
    },
    updateAuthInfo: (state, action: { payload: LoginResponse }) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.user = action.payload.user;

      setLastActiveUser(action.payload.user.id);
      setLoaclStorageAuthState(action.payload);
    },
  },
  extraReducers: builder => {
    builder.addCase(loginUserAction.pending, state => {
      state.isLoading = true;
    });

    builder.addCase(loginUserAction.fulfilled, handleLogin);
    builder.addCase(refreshAccessTokenAction.fulfilled, handleLogin);

    builder.addCase(loginUserAction.rejected, (state, action) => {
      state.isLoading = false;
      state.errors = action.payload ?? {};
    });

    builder.addCase(registerUserAction.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(registerUserAction.fulfilled, (state, action) => {
      state.isLoading = false;
      if (!action.payload) {
        return;
      }
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    });
    builder.addCase(registerUserAction.rejected, (state, action) => {
      state.isLoading = false;
      state.errors = action.payload ?? {};
    });
  },
});

export const { logout, updateAuthInfo } = authSlice.actions;

export const authReducer = authSlice.reducer;
